import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import SearchIcon from '@mui/icons-material/Search';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  GridAggregationFunction,
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { CircularProgress, LinearProgress } from '@mui/material';
import client from '../../util/api-client';
import type {
  Vendor,
  VendorPaymentCreateInput,
  VendorPaymentGroupedSearchInput,
  VendorPaymentPurchaseOrderGroup,
  VendorPaymentSearchVendor,
} from '@repo/api-contract';
import { pushToast, useDispatch } from '../../state';
import { openSearchVendorModal } from '../../state/vendorSearchModalSlice';
import SelectSearchVendor from '../../components/utils/SelectSearchVendor';
import { topNavBarHeight } from '../../components/NavBars/constants';
import DraftPaymentsSidebar from './DraftPaymentsSidebar';

type VendorPaymentDraftRow = VendorPaymentCreateInput & {
  id: string;
  isNew: true;
};

type PaymentTableRow = {
  id: string;
  isNew: boolean;
  vendorAccountNumber: string;
  vendorName: string;
  purchaseOrderCode: string;
  purchaseOrderDate: number;
  openingBalance: number;
  payment: number;
  reference: string;
  adjustment: number;
  detail: string;
  captureDate: number;
  resultingBalance: number;
  groupPurchaseOrderBalance?: number;
  groupResultingBalance?: number;
  isEmptyPurchaseOrder?: boolean;
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const formatCurrency = (value: number | undefined) =>
  value !== undefined ? currencyFormatter.format(value) : '';

const formatDate = (value: number | undefined) =>
  value ? new Date(value * 1000).toLocaleDateString() : '';

const PAYMENT_GROUP_PAGE_SIZE = 15;

function PurchaseOrderGroupActionButton({
  purchaseOrderCode,
  onSelect,
}: {
  purchaseOrderCode: string;
  onSelect: (purchaseOrderCode: string) => void;
}) {
  const { t } = useTranslation([], {
    keyPrefix: 'team.Components.PayVendor',
  });
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = () => {
    onSelect(purchaseOrderCode);
    handleClose();
  };

  return (
    <>
      <IconButton
        aria-controls={open ? 'pay-vendor-purchase-order-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label={t('Purchase order actions')}
        size="small"
        onClick={handleOpen}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        id="pay-vendor-purchase-order-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'pay-vendor-purchase-order-actions',
        }}
      >
        <MenuItem onClick={handleSelect}>{t('Use purchase order')}</MenuItem>
      </Menu>
    </>
  );
}

function PaymentRowActionButton({
  disabled,
  onDelete,
}: {
  disabled: boolean;
  onDelete: () => void;
}) {
  const { t } = useTranslation([], {
    keyPrefix: 'team.Components.PayVendor',
  });
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    onDelete();
    handleClose();
  };

  return (
    <>
      <IconButton
        aria-controls={open ? 'pay-vendor-payment-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label={t('Payment actions')}
        disabled={disabled}
        size="small"
        onClick={handleOpen}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        id="pay-vendor-payment-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'pay-vendor-payment-actions',
        }}
      >
        <MenuItem
          disabled={disabled}
          onClick={handleDelete}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {t('Delete')}
        </MenuItem>
      </Menu>
    </>
  );
}

export default function PayVendor() {
  const { t } = useTranslation([], {
    keyPrefix: 'team.Components.PayVendor',
  });
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const expandedPurchaseOrderCodesRef = React.useRef<Set<string>>(new Set());

  const [searchBy, setSearchBy] = useState<
    'vendorAccountNumber' | 'purchaseOrderCode'
  >('vendorAccountNumber');
  const [searchInput, setSearchInput] = useState('');
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextAfterKey, setNextAfterKey] = useState<
    Record<string, string> | undefined
  >();
  const [groups, setGroups] = useState<VendorPaymentPurchaseOrderGroup[]>([]);
  const [searchedVendor, setSearchedVendor] =
    useState<VendorPaymentSearchVendor | null>(null);
  const [paymentsToCreate, setPaymentsToCreate] = useState<
    VendorPaymentDraftRow[]
  >([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [selectedPurchaseOrderCode, setSelectedPurchaseOrderCode] = useState<
    string | null
  >(null);
  const [deletingPaymentIds, setDeletingPaymentIds] = useState<Set<string>>(
    () => new Set(),
  );

  const groupsByPurchaseOrder = useMemo(
    () => new Map(groups.map((group) => [group.purchaseOrderCode, group])),
    [groups],
  );

  // The opening/resulting balances are group-level values (not sums of the
  // leaf payments), so we expose them through custom aggregation functions.
  // This gives each group row a real numeric value that the grid can sort and
  // filter on, instead of an undefined cell.
  const aggregationFunctions = useMemo(() => {
    const groupValue = (
      selector: (group: VendorPaymentPurchaseOrderGroup) => number,
    ): GridAggregationFunction<number, number | null> => ({
      label: '',
      columnTypes: ['number'],
      apply: ({ groupId }) => {
        const node = apiRef.current.getRowNode(groupId) as {
          type?: string;
          groupingKey?: unknown;
        } | null;
        if (node?.type !== 'group' || node.groupingKey == null) return null;
        const group = groupsByPurchaseOrder.get(String(node.groupingKey));
        return group ? selector(group) : null;
      },
    });

    return {
      ...GRID_AGGREGATION_FUNCTIONS,
      sum: {
        ...GRID_AGGREGATION_FUNCTIONS.sum,
        label: '',
      },
      poOpeningBalance: groupValue((group) => group.purchaseOrderBalance),
      poResultingBalance: groupValue((group) => group.resultingBalance),
    };
  }, [apiRef, groupsByPurchaseOrder]);

  const selectedPurchaseOrder = selectedPurchaseOrderCode
    ? groupsByPurchaseOrder.get(selectedPurchaseOrderCode) ?? null
    : null;
  const dateRangeInvalid = Boolean(dateFrom && dateTo && dateFrom > dateTo);

  const allPayments = useMemo(
    () => groups.flatMap((group) => group.payments),
    [groups],
  );
  const totalPayments =
    allPayments.reduce((sum, payment) => sum + payment.payment, 0) +
    paymentsToCreate.reduce((sum, payment) => sum + payment.payment, 0);
  const totalAdjustments =
    allPayments.reduce((sum, payment) => sum + payment.adjustment, 0) +
    paymentsToCreate.reduce((sum, payment) => sum + payment.adjustment, 0);

  const vendorDisplayName = searchedVendor
    ? searchedVendor.contactName
      ? `${searchedVendor.vendorName} - ${searchedVendor.contactName}`
      : searchedVendor.vendorName
    : '';

  const handleVendorChange = (vendor: Vendor | null) => {
    setSelectedVendor(vendor);
    if (!vendor) {
      setGroups([]);
      setNextAfterKey(undefined);
      setSearchedVendor(null);
      setPaymentsToCreate([]);
      setRowModesModel({});
      setSelectedPurchaseOrderCode(null);
    }
  };

  const openBrowseVendorModal = () => {
    dispatch(
      openSearchVendorModal({
        onImport: (rows) => {
          const v = rows[0];
          if (v) setSelectedVendor(v);
        },
        onCancel: () => {},
        multiSelect: false,
      }),
    );
  };

  const handleSearch = useCallback(async () => {
    if (dateRangeInvalid) return;
    if (searchBy === 'vendorAccountNumber') {
      if (!selectedVendor?.details?.accountNumber?.trim()) return;
    } else {
      if (!searchInput.trim()) return;
    }
    setLoading(true);
    setSelectedPurchaseOrderCode(null);
    try {
      const query =
        searchBy === 'vendorAccountNumber'
          ? selectedVendor!.details.accountNumber
          : searchInput;
      const payload: VendorPaymentGroupedSearchInput = {
        query,
        searchBy,
        size: PAYMENT_GROUP_PAGE_SIZE,
        ...(searchBy === 'vendorAccountNumber' && dateFrom
          ? { dateFrom: new Date(dateFrom).getTime() / 1000 }
          : {}),
        ...(searchBy === 'vendorAccountNumber' && dateTo
          ? { dateTo: new Date(dateTo).getTime() / 1000 }
          : {}),
      };
      const response = await client.searchVendorPaymentsGrouped.query(payload);
      setGroups(response.groups);
      setNextAfterKey(response.afterKey);
      setSearchedVendor(response.vendor ?? null);
    } catch (error) {
      console.error('Error searching grouped vendor payments', error);
      setGroups([]);
      setNextAfterKey(undefined);
      setSearchedVendor(null);
    } finally {
      setLoading(false);
    }
  }, [
    selectedVendor,
    searchInput,
    searchBy,
    dateFrom,
    dateTo,
    dateRangeInvalid,
  ]);

  const handleLoadMoreGroups = useCallback(async () => {
    if (!nextAfterKey || loading || loadingMore || dateRangeInvalid) return;
    if (searchBy === 'vendorAccountNumber') {
      if (!selectedVendor?.details?.accountNumber?.trim()) return;
    } else {
      if (!searchInput.trim()) return;
    }

    setLoadingMore(true);
    try {
      const query =
        searchBy === 'vendorAccountNumber'
          ? selectedVendor!.details.accountNumber
          : searchInput;
      const payload: VendorPaymentGroupedSearchInput = {
        query,
        searchBy,
        size: PAYMENT_GROUP_PAGE_SIZE,
        afterKey: nextAfterKey,
        ...(searchBy === 'vendorAccountNumber' && dateFrom
          ? { dateFrom: new Date(dateFrom).getTime() / 1000 }
          : {}),
        ...(searchBy === 'vendorAccountNumber' && dateTo
          ? { dateTo: new Date(dateTo).getTime() / 1000 }
          : {}),
      };
      const response = await client.searchVendorPaymentsGrouped.query(payload);
      let addedCount = 0;
      setGroups((prev) => {
        const purchaseOrderCodes = new Set(
          prev.map((group) => group.purchaseOrderCode),
        );
        const newGroups = response.groups.filter(
          (group) => !purchaseOrderCodes.has(group.purchaseOrderCode),
        );
        addedCount = newGroups.length;
        return [...prev, ...newGroups];
      });
      // Stop paginating when the backend has nothing more to give us.
      const reachedEnd =
        addedCount === 0 ||
        response.groups.length < PAYMENT_GROUP_PAGE_SIZE ||
        !response.afterKey;
      setNextAfterKey(reachedEnd ? undefined : response.afterKey);
      setSearchedVendor((prev) => prev ?? response.vendor ?? null);
    } catch (error) {
      console.error('Error loading more grouped vendor payments', error);
      dispatch(
        pushToast({
          message: t('Unable to load more payments'),
          type: 'error',
          duration: 7000,
        }),
      );
    } finally {
      setLoadingMore(false);
    }
  }, [
    nextAfterKey,
    loading,
    loadingMore,
    dateRangeInvalid,
    searchBy,
    selectedVendor,
    searchInput,
    dateFrom,
    dateTo,
    dispatch,
    t,
  ]);

  // `onRowsScrollEnd` is unreliable with row grouping (it often never fires
  // when the collapsed groups already fit in the viewport), so we attach our
  // own scroll listener to the grid's virtual scroller instead.
  React.useEffect(() => {
    const root = apiRef.current?.rootElementRef?.current;
    if (!root) return;

    const scroller = root.querySelector<HTMLDivElement>(
      '.MuiDataGrid-virtualScroller',
    );
    if (!scroller) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scroller;
      if (scrollHeight - scrollTop - clientHeight < 120) {
        handleLoadMoreGroups();
      }
    };

    scroller.addEventListener('scroll', handleScroll);
    return () => scroller.removeEventListener('scroll', handleScroll);
  }, [apiRef, handleLoadMoreGroups, groups.length, searchedVendor]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleRecord = useCallback(async () => {
    // TODO: open a form dialog for recording a new payment or submit selected rows
    console.info('Record button clicked');
  }, []);

  const handleNew = useCallback(() => {
    if (!searchedVendor) return;
    const id = `draft-${uuidv4()}`;
    const newRow: VendorPaymentDraftRow = {
      id,
      isNew: true,
      vendorAccountNumber: searchedVendor.vendorAccountNumber,
      vendorName: searchedVendor.vendorName,
      purchaseOrderCode: '',
      payment: 0,
      reference: '',
      adjustment: 0,
      detail: '',
      captureDate: Math.floor(Date.now() / 1000),
    };
    setPaymentsToCreate((prev) => [...prev, newRow]);
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit },
    }));
  }, [searchedVendor]);

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleSelectPurchaseOrder = useCallback((purchaseOrderCode: string) => {
    setSelectedPurchaseOrderCode(purchaseOrderCode);
  }, []);

  const getGroupRowNode = useCallback(
    (id: GridRowId) =>
      apiRef.current.getRowNode(id) as {
        type?: string;
        groupingKey?: unknown;
        childrenExpanded?: boolean;
      } | null,
    [apiRef],
  );

  const getPurchaseOrderCodeFromGroupRow = useCallback(
    (id: GridRowId) => {
      const rowNode = getGroupRowNode(id);

      if (rowNode?.type !== 'group' || rowNode.groupingKey == null) {
        return null;
      }

      return String(rowNode.groupingKey);
    },
    [getGroupRowNode],
  );

  const captureExpandedPurchaseOrderCodes = useCallback(() => {
    const expandedPurchaseOrderCodes = new Set<string>();

    apiRef.current.getAllRowIds().forEach((id) => {
      const rowNode = getGroupRowNode(id);
      if (
        rowNode?.type === 'group' &&
        rowNode.childrenExpanded &&
        rowNode.groupingKey != null
      ) {
        expandedPurchaseOrderCodes.add(String(rowNode.groupingKey));
      }
    });

    expandedPurchaseOrderCodesRef.current = expandedPurchaseOrderCodes;
  }, [apiRef, getGroupRowNode]);

  const restoreExpandedPurchaseOrderCodes = useCallback(() => {
    const expandedPurchaseOrderCodes = expandedPurchaseOrderCodesRef.current;
    if (expandedPurchaseOrderCodes.size === 0) return;

    apiRef.current.getAllRowIds().forEach((id) => {
      const rowNode = getGroupRowNode(id);
      if (
        rowNode?.type === 'group' &&
        rowNode.groupingKey != null &&
        expandedPurchaseOrderCodes.has(String(rowNode.groupingKey))
      ) {
        apiRef.current.setRowChildrenExpansion(id, true);
      }
    });
  }, [apiRef, getGroupRowNode]);

  const handleDeleteClick = (id: GridRowId) => () => {
    setPaymentsToCreate((prev) => prev.filter((row) => row.id !== id));
    setRowModesModel((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleDeletePayment = useCallback(
    async (row: PaymentTableRow) => {
      if (row.isNew || deletingPaymentIds.has(row.id)) return;

      setDeletingPaymentIds((prev) => new Set(prev).add(row.id));
      try {
        await client.deleteVendorPayment.mutate({
          VendorPaymentId: row.id,
        });
        captureExpandedPurchaseOrderCodes();
        setGroups((prev) =>
          prev.flatMap((group) => {
            if (group.purchaseOrderCode !== row.purchaseOrderCode)
              return [group];

            const payments = group.payments.filter(
              (payment) => payment.VendorPaymentId !== row.id,
            );
            if (payments.length === 0) return [];

            const totalPayment = group.totalPayment - row.payment;
            const totalAdjustment = group.totalAdjustment - row.adjustment;

            return [
              {
                ...group,
                payments,
                totalPayment,
                totalAdjustment,
                resultingBalance:
                  group.purchaseOrderBalance - totalPayment - totalAdjustment,
              },
            ];
          }),
        );
        requestAnimationFrame(restoreExpandedPurchaseOrderCodes);
        dispatch(
          pushToast({
            message: t(
              'Payment for purchase order {{purchaseOrderCode}} deleted',
              {
                purchaseOrderCode: row.purchaseOrderCode,
              },
            ),
            type: 'success',
            duration: 7000,
          }),
        );
      } catch (error) {
        console.error('Error deleting vendor payment', error);
        dispatch(
          pushToast({
            message: t(
              'Unable to delete payment for purchase order {{purchaseOrderCode}}',
              {
                purchaseOrderCode: row.purchaseOrderCode,
              },
            ),
            type: 'error',
            duration: 7000,
          }),
        );
      } finally {
        setDeletingPaymentIds((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
      }
    },
    [
      deletingPaymentIds,
      dispatch,
      captureExpandedPurchaseOrderCodes,
      restoreExpandedPurchaseOrderCodes,
      t,
    ],
  );

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleRowModesModelChange = (newModel: GridRowModesModel) => {
    setRowModesModel(newModel);
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: true } as VendorPaymentDraftRow;
    setPaymentsToCreate((prev) =>
      prev.map((row) => (row.id === newRow.id ? updatedRow : row)),
    );
    return updatedRow;
  };

  const numberTypeController = (value: any) =>
    value < 0 ? 0 : parseFloat(value);

  const tableRows = useMemo<PaymentTableRow[]>(() => {
    const purchaseOrderRows: PaymentTableRow[] = groups.flatMap((group) => {
      if (group.payments.length === 0) {
        return [
          {
            id: `empty-purchase-order-${group.purchaseOrderCode}`,
            isNew: false,
            isEmptyPurchaseOrder: true,
            vendorAccountNumber: searchedVendor?.vendorAccountNumber ?? '',
            vendorName: searchedVendor?.vendorName ?? '',
            purchaseOrderCode: group.purchaseOrderCode,
            purchaseOrderDate: group.purchaseOrderDate,
            openingBalance: group.purchaseOrderBalance,
            payment: 0,
            reference: '',
            adjustment: 0,
            detail: '',
            captureDate: 0,
            resultingBalance: group.resultingBalance,
            groupPurchaseOrderBalance: group.purchaseOrderBalance,
            groupResultingBalance: group.resultingBalance,
          },
        ];
      }

      return group.payments.map(
        (payment): PaymentTableRow => ({
          id: payment.VendorPaymentId,
          ...payment,
          isNew: false,
          groupPurchaseOrderBalance: group.purchaseOrderBalance,
          groupResultingBalance: group.resultingBalance,
        }),
      );
    });

    return [
      ...purchaseOrderRows,
      ...paymentsToCreate.map((payment) => ({
        ...payment,
        purchaseOrderDate: 0,
        openingBalance: 0,
        resultingBalance: 0,
        isNew: true,
      })),
    ];
  }, [groups, paymentsToCreate, searchedVendor]);

  const renderGroupAwareCell = useCallback(
    (
      params: GridRenderCellParams<PaymentTableRow>,
      renderLeaf: (row: PaymentTableRow) => React.ReactNode,
    ) => {
      if (params.rowNode.type === 'group') {
        const group = groupsByPurchaseOrder.get(
          String(params.rowNode.groupingKey),
        );
        if (!group) return null;

        switch (params.field) {
          case 'purchaseOrderDate':
            return formatDate(group.purchaseOrderDate);
          case 'openingBalance':
            return formatCurrency(group.purchaseOrderBalance);
          case 'resultingBalance':
            return formatCurrency(group.resultingBalance);
          default:
            return null;
        }
      }

      return params.row.isEmptyPurchaseOrder ? null : renderLeaf(params.row);
    },
    [groupsByPurchaseOrder],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'vendorAccountNumber',
        headerName: t('Account Number'),
        width: 130,
      },
      {
        field: 'vendorName',
        headerName: t('Vendor Name'),
        width: 180,
      },
      {
        field: 'purchaseOrderCode',
        headerName: t('Purchase Order No.'),
        width: 170,
        editable: true,
      },
      {
        field: 'purchaseOrderDate',
        headerName: t('Purchase Order Date'),
        width: 120,
        renderCell: (params) =>
          renderGroupAwareCell(params, (row) =>
            formatDate(row.purchaseOrderDate),
          ),
      },
      {
        field: 'openingBalance',
        headerName: t('Opening Balance'),
        width: 140,
        type: 'number',
        renderCell: (params) =>
          renderGroupAwareCell(params, (row) =>
            formatCurrency(row.openingBalance),
          ),
      },
      {
        field: 'payment',
        headerName: t('Payment'),
        width: 110,
        type: 'number',
        editable: true,
        aggregable: true,
        valueParser: numberTypeController,
        valueFormatter: (value: number) => formatCurrency(value),
        renderCell: (params) =>
          params.row.isEmptyPurchaseOrder
            ? null
            : formatCurrency(params.row.payment),
      },
      {
        field: 'reference',
        headerName: t('Reference'),
        width: 130,
        editable: true,
      },
      {
        field: 'adjustment',
        headerName: t('Adjustment'),
        width: 120,
        type: 'number',
        editable: true,
        aggregable: true,
        valueParser: numberTypeController,
        valueFormatter: (value: number) => formatCurrency(value),
        renderCell: (params) =>
          params.row.isEmptyPurchaseOrder
            ? null
            : formatCurrency(params.row.adjustment),
      },
      {
        field: 'detail',
        headerName: t('Detail'),
        width: 150,
        editable: true,
      },
      {
        field: 'captureDate',
        headerName: t('Date'),
        width: 140,
        editable: true,
        type: 'date',
        valueGetter: (value: number) => (value ? new Date(value * 1000) : null),
        valueSetter: (value: Date | null, row: any) => ({
          ...row,
          captureDate: value ? Math.floor(value.getTime() / 1000) : 0,
        }),
      },
      {
        field: 'resultingBalance',
        headerName: t('Resulting Balance'),
        width: 150,
        type: 'number',
        renderCell: (params) =>
          renderGroupAwareCell(params, (row) =>
            formatCurrency(row.resultingBalance),
          ),
      },
      {
        field: 'actions',
        headerName: t('Actions'),
        width: 80,
        cellClassName: 'actions',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        display: 'flex',
        renderCell: (params) => {
          const purchaseOrderCode = getPurchaseOrderCodeFromGroupRow(params.id);

          if (purchaseOrderCode) {
            return (
              <PurchaseOrderGroupActionButton
                purchaseOrderCode={purchaseOrderCode}
                onSelect={handleSelectPurchaseOrder}
              />
            );
          }

          if (params.row.isEmptyPurchaseOrder) {
            return null;
          }

          if (!params.row.isNew) {
            return (
              <PaymentRowActionButton
                disabled={deletingPaymentIds.has(params.row.id)}
                onDelete={() => handleDeletePayment(params.row)}
              />
            );
          }

          const isInEditMode =
            rowModesModel[params.id]?.mode === GridRowModes.Edit;

          if (isInEditMode) {
            return (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  aria-label={t('Save')}
                  size="small"
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSaveClick(params.id)();
                  }}
                >
                  <SaveIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label={t('Cancel')}
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCancelClick(params.id)();
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          }

          return (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                aria-label={t('Edit')}
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleEditClick(params.id)();
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                aria-label={t('Delete')}
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeleteClick(params.id)();
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [
      rowModesModel,
      renderGroupAwareCell,
      handleSelectPurchaseOrder,
      getPurchaseOrderCodeFromGroupRow,
      deletingPaymentIds,
      handleDeletePayment,
      t,
    ],
  );

  return (
    <div className="flex flex-row w-full min-w-0 overflow-hidden mx-10 my-12 md:mx-20 flex-1 gap-4">
      <div className="flex flex-col flex-1 min-w-0 gap-4">
        {/* Search/Filter Section */}
        <div className="mx-4 mb-2 p-4 border border-gray-200 border-solid rounded shadow-xl">
          <h2 className="text-blue-600">{t('Pay Vendor')}</h2>
          <hr />
          <Box
            className="mt-5"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 3,
              height: 76,
            }}
          >
            <FormControl>
              <FormLabel>{t('Search by')}</FormLabel>
              <RadioGroup
                row
                value={searchBy}
                onChange={(e) => {
                  setSearchBy(
                    e.target.value as
                      | 'vendorAccountNumber'
                      | 'purchaseOrderCode',
                  );
                  setGroups([]);
                  setNextAfterKey(undefined);
                  setSearchedVendor(null);
                  setSearchInput('');
                  setSelectedVendor(null);
                  setPaymentsToCreate([]);
                  setRowModesModel({});
                  setSelectedPurchaseOrderCode(null);
                }}
              >
                <FormControlLabel
                  value="vendorAccountNumber"
                  control={<Radio size="small" />}
                  label={t('Vendor')}
                />
                <FormControlLabel
                  value="purchaseOrderCode"
                  control={<Radio size="small" />}
                  label={t('Purchase Order No.')}
                />
              </RadioGroup>
            </FormControl>

            {searchBy === 'vendorAccountNumber' ? (
              <Stack sx={{ minWidth: 280 }} gap={0.5}>
                <SelectSearchVendor
                  label={t('Vendor')}
                  value={selectedVendor}
                  onChange={handleVendorChange}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Link
                    underline="always"
                    sx={{ cursor: 'pointer' }}
                    onClick={openBrowseVendorModal}
                  >
                    {t('Browse Vendor')}
                  </Link>
                </Box>
              </Stack>
            ) : (
              <TextField
                size="small"
                label={t('Search by purchase order number...')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                sx={{ minWidth: 260 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {searchBy === 'vendorAccountNumber' && (
              <>
                <TextField
                  size="small"
                  label={t('From')}
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  error={dateRangeInvalid}
                  helperText={
                    dateRangeInvalid ? t('From must be before To') : ''
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: dateTo || undefined }}
                  sx={{ width: 160 }}
                />
                <TextField
                  size="small"
                  label={t('To')}
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  error={dateRangeInvalid}
                  helperText={
                    dateRangeInvalid ? t('To must be after From') : ''
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: dateFrom || undefined }}
                  sx={{ width: 160 }}
                />
              </>
            )}

            <Button
              variant="outlined"
              color="primary"
              disabled={dateRangeInvalid}
              onClick={handleSearch}
            >
              {t('Search')}
            </Button>
          </Box>
        </div>

        {/* Data Table */}
        <div className="mx-4 flex flex-col flex-1 min-w-0 overflow-hidden border border-gray-200 border-solid rounded shadow-xl">
          {searchedVendor ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                px: 2,
                py: 3,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <Chip
                    label={searchedVendor.vendorAccountNumber}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 400 }}
                  />
                  <Typography variant="h5">{vendorDisplayName}</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    mt: 1,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  {searchedVendor.email && (
                    <>
                      <EmailOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'text.secondary' }}
                      />
                      <Link
                        href={`mailto:${searchedVendor.email}`}
                        underline="always"
                        color="text.secondary"
                        variant="body1"
                      >
                        {searchedVendor.email}
                      </Link>
                    </>
                  )}
                  {searchedVendor.email && searchedVendor.phone && (
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ mx: 0.5, borderColor: 'divider' }}
                    />
                  )}
                  {searchedVendor.phone && (
                    <>
                      <PhoneOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'text.secondary' }}
                      />
                      <Typography variant="body1" color="text.secondary">
                        {searchedVendor.phone}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box height={`95px`} />
          )}
          <div
            style={{
              height: `calc(100vh - ${topNavBarHeight} - 450px)`,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            <DataGridPremium
              apiRef={apiRef}
              rows={tableRows}
              columns={columns}
              loading={loading}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              isCellEditable={(params) =>
                params.row.isNew === true && !params.row.isEmptyPurchaseOrder
              }
              onRowDoubleClick={(params) => {
                const purchaseOrderCode = getPurchaseOrderCodeFromGroupRow(
                  params.id,
                );
                if (purchaseOrderCode) {
                  handleSelectPurchaseOrder(purchaseOrderCode);
                }
              }}
              onRowsScrollEnd={handleLoadMoreGroups}
              disableRowSelectionOnClick
              rowGroupingModel={['purchaseOrderCode']}
              groupingColDef={{
                headerName: t('Purchase Order No.'),
                width: 180,
                hideDescendantCount: true,
                sortComparator: (a, b) => Number(a) - Number(b),
                valueFormatter: (value) =>
                  value != null && value !== '' ? `${t('PO')} ${value}` : '',
              }}
              aggregationFunctions={aggregationFunctions}
              defaultGroupingExpansionDepth={0}
              hideFooter={true}
              columnHeaderHeight={56}
              pinnedColumns={{ right: ['actions'] }}
              sx={{
                flex: 1,
                width: '100%',
                minHeight: 0,
                minWidth: 0,
                border: 'none',
                padding: '1rem',
                '.MuiDataGrid-row:nth-of-type(even)': {
                  backgroundColor: '#ECECEC',
                },
                '.MuiDataGrid-row:nth-of-type(even).MuiDataGrid-row--grouping':
                  {
                    backgroundColor: '#E3EEF9',
                  },
                '.MuiDataGrid-row.MuiDataGrid-row--grouping': {
                  backgroundColor: '#E3EEF9',
                  fontWeight: 600,
                },
                '.MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold',
                  whiteSpace: 'normal',
                  lineHeight: 1.2,
                },
              }}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    purchaseOrderCode: false,
                  },
                },
                rowGrouping: {
                  model: ['purchaseOrderCode'],
                },
                sorting: {
                  sortModel: [{ field: 'captureDate', sort: 'desc' }],
                },
                aggregation: {
                  model: {
                    payment: 'sum',
                    adjustment: 'sum',
                    openingBalance: 'poOpeningBalance',
                    resultingBalance: 'poResultingBalance',
                  },
                },
                pagination: {
                  paginationModel: { pageSize: 100 },
                },
              }}
              pageSizeOptions={[100]}
              slots={{
                loadingOverlay: () => <LinearProgress color="primary" />,
              }}
            />
            {loadingMore ? (
              <Box
                sx={{
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : null}
          </div>
        </div>
      </div>

      <DraftPaymentsSidebar
        selectedPurchaseOrder={selectedPurchaseOrder}
        searchedVendor={searchedVendor}
        onDraftCreated={() => setSelectedPurchaseOrderCode(null)}
      />
    </div>
  );
}
